import React from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Container,
    Paper,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchLedgerTypes, setSelectedType } from '../../store/slices/headOfficeFinanceSlice';
import HeadOfficeLedgerTab from './components/HeadOfficeLedgerTab';
import HeadOfficeSummaryTab from './components/HeadOfficeSummaryTab';

class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; error: Error | null }
> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Head Office Finances error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <Alert severity="error">
                    Something went wrong: {this.state.error?.message || 'Unknown error'}
                </Alert>
            );
        }

        return this.props.children;
    }
}

const HeadOfficeFinances: React.FC = () => {
    const dispatch = useAppDispatch();
    const { ledgerTypes, selectedType, loading, error } = useAppSelector(
        (state) => state.headOfficeFinance
    );

    const [activeTab, setActiveTab] = React.useState<string>('summary');

    React.useEffect(() => {
        dispatch(fetchLedgerTypes());
    }, [dispatch]);

    // Set first ledger type as selected when loaded
    React.useEffect(() => {
        if (ledgerTypes.length > 0 && !selectedType) {
            dispatch(setSelectedType(ledgerTypes[0].slug));
        }
    }, [ledgerTypes, selectedType, dispatch]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
        setActiveTab(newValue);
        if (newValue !== 'summary') {
            dispatch(setSelectedType(newValue));
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Head Office Finances
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    View and manage head office ledger entries across different account types.
                </Typography>

                {loading && ledgerTypes.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress />
                    </Box>
                )}

                <Paper elevation={1} sx={{ borderRadius: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="Head office finance tabs"
                    >
                        <Tab value="summary" label="Summary" />
                        {ledgerTypes.map((type) => (
                            <Tab key={type.slug} value={type.slug} label={type.name} />
                        ))}
                    </Tabs>
                </Paper>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ minHeight: 400 }}>
                {activeTab === 'summary' ? (
                    <React.Suspense fallback={<CircularProgress />}>
                        <ErrorBoundary>
                            <HeadOfficeSummaryTab />
                        </ErrorBoundary>
                    </React.Suspense>
                ) : (
                    <React.Suspense fallback={<CircularProgress />}>
                        <ErrorBoundary>
                            <HeadOfficeLedgerTab ledgerType={activeTab} />
                        </ErrorBoundary>
                    </React.Suspense>
                )}
            </Box>
        </Container>
    );
};

export default HeadOfficeFinances;
